import type { DateTime } from 'luxon';

import type { CandlestickGraphPoint } from 'components/Graph/components/types';
import type { PERIOD_TYPE } from 'constants/charting';
import type { CryptoCoin } from 'models/underlying';
import type { ExternalSourceHistoricalData } from 'queries/externalSource';

// 15-minute knockout series, split by asset class because the two are served differently: crypto has a
// public-docs lane driving the live view, commodities are Pyth-backed and event-scoped with no such lane.
// Only the crypto half belongs in `KNOCKOUT_CRYPTO_TICKERS`, which is why these are separate lists rather
// than one flat allowlist.
export const CRYPTO_KNOCKOUT_15M_TICKERS = [
    'KXBTC15M',
    'KXETH15M',
    'KXSOL15M',
    'KXXRP15M',
    'KXDOGE15M',
    'KXBNB15M',
    'KXHYPE15M',
    'KXZEC15M',
    'KXNEAR15M',
] as const;

export const COMMODITY_KNOCKOUT_15M_TICKERS = [
    'KXGOLD15M',
    'KXSILVER15M',
    'KXWTI15M',
    'KXPALLADIUM15M',
    'KXPLATINUM15M',
    'KXCOPPER15M',
    'KXNATGAS15M',
    // Treasury yields use the same Pyth-backed asset lane, in percentage units.
    'KX2YRRATE15M',
    'KX5YRRATE15M',
    'KX10YRRATE15M',
    'KX30YRRATE15M',
    // FX uses the same Pyth-backed live-data lane as commodities.
    'KXEURUSD15M',
    'KXGBPUSD15M',
    'KXUSDJPY15M',
    'KXAUDUSD15M',
    'KXUSDCAD15M',
] as const;

// Crypto Leader: which asset gains the most over the window. It shares the 15-minute knockout page
// (chart type, chart height, time nav, price label) but is a race between the event's markets
// rather than one asset against a target, so it has no single underlying and no per-asset colour.
// Kept out of the two lists above because those carry exactly that per-asset meaning.
export const MULTI_ASSET_KNOCKOUT_15M_TICKERS = ['KXCRYPTOLEAD15M'] as const;

export const KNOCKOUT_15M_TICKERS = [
    ...CRYPTO_KNOCKOUT_15M_TICKERS,
    ...COMMODITY_KNOCKOUT_15M_TICKERS,
    ...MULTI_ASSET_KNOCKOUT_15M_TICKERS,
] as const;

/**
 * Directional knockouts: one underlying measured against a strike, so "yes" means it finished
 * above and the UI can legitimately relabel the sides Up/Down.
 *
 * The race is excluded on purpose. Its markets are contenders, not directions: "yes" means that
 * asset gained the most over the window, and "no" means it did not. Calling those Up and Down on
 * the order panel would describe a trade nobody is making. Chart routing and layout still want
 * the broad `is15MinuteKnockoutTicker`; only side COPY belongs here.
 */
export const isDirectionalKnockoutTicker = (seriesTicker: string): boolean => {
    const normalized = seriesTicker.toUpperCase();
    return (
        (CRYPTO_KNOCKOUT_15M_TICKERS as readonly string[]).includes(normalized) ||
        (COMMODITY_KNOCKOUT_15M_TICKERS as readonly string[]).includes(normalized)
    );
};

export const is15MinuteKnockoutTicker = (seriesTicker: string): boolean =>
    KNOCKOUT_15M_TICKERS.includes(seriesTicker.toUpperCase() as (typeof KNOCKOUT_15M_TICKERS)[number]);

// Knockout decimal count derived from the threshold so labels match the event title; floors at 2.
export const getKnockoutNaturalDecimals = (threshold: number | string | null | undefined): number => {
    if (threshold == null) return 2;
    const str = typeof threshold === 'number' ? threshold.toString() : threshold;
    return Math.max(2, (str.split('.')[1] ?? '').length);
};

// `floor_strike` is a JSON number, so trailing zeros are lost (e.g. 0.1036470 -> 0.103647).
// The market subtitle preserves them in dollar prices or percentage yields; prefer the wider count.
export const getKnockoutDecimalsFromSubtitle = (subtitle: string | null | undefined): number | undefined => {
    if (!subtitle) return undefined;
    const match = subtitle.match(/\$[\d,]+\.(\d+)|[\d,]+\.(\d+)%/);
    return match ? (match[1] ?? match[2]).length : undefined;
};

/**
 * All known crypto knockout series tickers (all frequencies).
 * Kept in sync with seriesTickerToFinancialName in Charts2/utils.ts.
 */
const KNOCKOUT_CRYPTO_TICKERS = new Set([
    'BTCD',
    'BTC',
    'KXBTCD',
    'KXBTC',
    'KXBTC15M',
    'ETHD',
    'ETH',
    'KXETHD',
    'KXETH',
    'KXETH15M',
    'KXDOGED',
    'KXDOGE',
    'KXDOGEMAXW',
    'KXDOGEMAXY',
    'KXDOGE15M',
    'KXBNBD',
    'KXBNB',
    'KXBNB15M',
    'KXHYPED',
    'KXHYPE',
    'KXHYPE15M',
    'KXZECD',
    'KXZEC',
    'KXZEC15M',
    'KXNEARD',
    'KXNEAR',
    'KXNEAR15M',
    'KXSHIBAD',
    'KXSHIBA',
    'KXSHIBAMAXW',
    'KXSHIBAMAXY',
    'SOLD',
    'SOL',
    'KXSOLD',
    'KXSOL',
    'KXSOL15M',
    'KXXRPD',
    'KXXRP',
    'KXXRP15M',
]);

/** Detects any crypto knockout market by series ticker (all frequencies). */
export const isKnockoutCryptoTicker = (seriesTicker: string): boolean =>
    KNOCKOUT_CRYPTO_TICKERS.has(seriesTicker.toUpperCase());

/**
 * Detects any binary knockout market by event title format: "X · $Y target".
 * Used for card design decisions (subtitle, countdown, progress bar colors) and
 * is intentionally separate from is15MinuteKnockoutTicker, which controls chart behavior.
 */
export const isKnockoutCardTitle = (eventTitle: string): boolean =>
    eventTitle.includes(' · ') && eventTitle.trimEnd().endsWith(' target');

/**
 * True when a 15-minute crypto knockout card should be treated as live based on
 * its market trading window. Used as a fallback when no milestone is attached
 * (milestone-driven cards keep the milestone path as source of truth).
 *
 * Deliberately does not look at the category. `is15MinuteKnockoutTicker` is an exact allowlist, so a
 * category check can only ever reject a card the allowlist already accepted, and it did: it started as
 * crypto-only and dropped open gold and silver cards out of the live buckets. Adding Commodities would
 * leave the same trap for the next series the backend files elsewhere, and WTI is already recorded under
 * Financials in parts of this repo.
 *
 * Inputs are nullable so callers can pass `series_ticker` and `firstMarket?.open_date / close_date`
 * directly without pre-validating.
 */
export function is15MinuteKnockoutLive({
    seriesTicker,
    openDate,
    closeDate,
    now,
}: {
    seriesTicker: string | undefined;
    openDate: string | undefined;
    closeDate: string | undefined;
    now: DateTime;
}): boolean {
    if (!seriesTicker || !is15MinuteKnockoutTicker(seriesTicker)) return false;
    if (!openDate || !closeDate) return false;
    // `Date.parse` rather than `DateTime.fromISO`: the caller already supplied `now`, so the only
    // thing Luxon's parser adds here is two DateTime objects (and the clock read it makes while
    // defaulting the units the input omits) for a pair of comparisons. NaN from a malformed date
    // compares false throughout, which is what an invalid DateTime did.
    const startMs = Date.parse(openDate);
    const endMs = Date.parse(closeDate);
    const nowMs = now.toMillis();
    return startMs <= nowMs && nowMs <= endMs;
}

export const FINANCIAL_NAME_TO_COIN: Partial<Record<string, CryptoCoin>> = {
    BTC: 'BTC',
    ETH: 'ETH',
    DOGE: 'DOGE',
    SHIBA: 'SHIBA',
    SOL: 'SOL',
    XRP: 'XRP',
    BNB: 'BNB',
    HYPE: 'HYPE',
    ZEC: 'ZEC',
    NEAR: 'NEAR',
};

export const fetchCryptoHistoricalData = (
    historicalData: ExternalSourceHistoricalData | undefined,
    period: PERIOD_TYPE,
): [CandlestickGraphPoint[], CandlestickGraphPoint | undefined, string | undefined] => {
    const candlesticksBySize = historicalData?.candlesticksBySize ?? {};

    // Candle size per window. Anything unmapped falls through to 6H, which over a short window collapses the
    // whole chart into one or two aggregate points, so every period the selectors offer must be listed here.
    const candleSizeForPeriod: Partial<Record<PERIOD_TYPE, keyof typeof candlesticksBySize>> = {
        '15min': '1M',
        '1h': '1M',
        '3h': '1M',
        '4h': '1M',
        '1d': '15M',
        '1w': '1H',
    };

    const historicalValuesForPeriod = (candlesticksBySize[candleSizeForPeriod[period] ?? '6H'] ?? []) as
        | CandlestickGraphPoint[]
        | [];

    const graphValues = historicalValuesForPeriod.filter((point) => point.closeValue && point.closeValue > 0);

    return [graphValues, graphValues[0], undefined];
};
